import {
  LuClipboardCheck,
  LuSlidersHorizontal,
  LuUser,
  LuCalendar,
  LuShieldCheck,
  LuShieldAlert,
} from "react-icons/lu";
import { PiWarningCircle } from "react-icons/pi";
import { GoAlert } from "react-icons/go";

type CertificationStatus = "Certified" | "Uncertified" | "Decertified";

type CertificationHeadingProps = {
  certificationMonth: string;
  certificationStatus: CertificationStatus | (string & {});
  certificationStep: string;
  lastReviewer: string;
  lastReviewDate: React.ReactNode;
  lastReviewDateString: string; // plain string for aria-label
  totalErrors: number;
  warnings: number;
};

const statusStyleMap: Record<
  CertificationStatus,
  { bg: string; color: string; border: string }
> = {
  Certified: { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
  Uncertified: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  Decertified: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
};

const defaultStatusStyle = {
  bg: "#f3f4f6",
  color: "#374151",
  border: "#e5e7eb",
};

const errorStyle = {
  active: {
    bg: "#fef2f2",
    border: "#fecaca",
    labelColor: "#991b1b",
    valueColor: "#dc2626",
  },
  neutral: {
    bg: "#f8fafc",
    border: "#ebebeb",
    labelColor: "#475569",
    valueColor: "#0f172a",
  },
};

const warningStyle = {
  active: {
    bg: "#fefce8",
    border: "#fde68a",
    labelColor: "#92400e",
    valueColor: "#d97706",
  },
  neutral: {
    bg: "#f8fafc",
    border: "#ebebeb",
    labelColor: "#475569",
    valueColor: "#0f172a",
  },
};

export default function CertificationHeading({
  certificationMonth,
  certificationStatus,
  certificationStep,
  lastReviewer,
  lastReviewDate,
  lastReviewDateString,
  totalErrors,
  warnings,
}: CertificationHeadingProps) {
  const statusStyle =
    statusStyleMap[certificationStatus as CertificationStatus] ??
    defaultStatusStyle;

  const StatusIcon =
    certificationStatus === "Certified" ? LuShieldCheck : LuShieldAlert;

  const errorColors =
    totalErrors === 0 ? errorStyle.neutral : errorStyle.active;
  const warningColors =
    warnings === 0 ? warningStyle.neutral : warningStyle.active;

  return (
    <>
      <div
        className="px-4 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(rgb(0, 58, 93) 0%, rgb(0, 38, 61) 100%)",
          color: "#fff",
          borderRadius: "0.375rem 0.375rem 0px 0px",
          paddingTop: "1.15rem",
          paddingBottom: "1.15rem",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3"
            style={{
              width: 44,
              height: 44,
              backgroundColor: "rgba(255,255,255,0.12)",
            }}
            aria-hidden="true"
          >
            <LuClipboardCheck size={22} />
          </div>
          <div>
            <p
              className="fw-bold text-uppercase mb-0"
              style={{
                fontSize: "0.75rem",
                letterSpacing: "1px",
                color: "rgba(255,255,255,0.85)",
              }}
              aria-hidden="true"
            >
              Certification Details
            </p>
            <h1
              className="fw-bold mt-1 mb-0"
              style={{ fontSize: "1.5rem", color: "#fff" }}
            >
              <span className="visually-hidden">Certification Details — </span>
              {certificationMonth}
            </h1>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.75)",
              whiteSpace: "nowrap",
            }}
          >
            Certification Status:
          </span>
          <span
            className="d-flex align-items-center gap-2 rounded-pill px-3 py-1"
            role="status"
            aria-label={`Certification status: ${certificationStatus}`}
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              border: `1px solid ${statusStyle.border}`,
              fontSize: "0.875rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            <StatusIcon size={15} aria-hidden="true" />
            {certificationStatus}
          </span>
        </div>
      </div>

      <div
        className="px-4"
        role="region"
        aria-label="Certification summary"
        style={{
          paddingTop: "calc(2.5rem - 2px)",
          paddingBottom: "calc(3rem - 2px)",
          marginBottom: "15px",
        }}
      >
        <div className="row align-items-stretch g-3">
          <div className="col d-flex">
            <div
              className="rounded-3 px-3 py-3 w-100 d-flex flex-column align-items-center justify-content-center text-center"
              role="group"
              aria-label={`Total Errors: ${totalErrors}`}
              style={{
                backgroundColor: errorColors.bg,
                border: `1px solid ${errorColors.border}`,
              }}
            >
              <div
                className="d-flex align-items-center gap-1 mb-2"
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 400,
                  color: errorColors.labelColor,
                }}
              >
                <PiWarningCircle size={15} aria-hidden="true" />
                Total Errors
              </div>
              <div
                aria-hidden="true"
                style={{
                  fontSize: "1.75rem",
                  fontWeight: 600,
                  color: errorColors.valueColor,
                }}
              >
                {totalErrors}
              </div>
            </div>
          </div>

          <div className="col d-flex">
            <div
              className="rounded-3 px-3 py-3 w-100 d-flex flex-column align-items-center justify-content-center text-center"
              role="group"
              aria-label={`Warnings: ${warnings}`}
              style={{
                backgroundColor: warningColors.bg,
                border: `1px solid ${warningColors.border}`,
              }}
            >
              <div
                className="d-flex align-items-center gap-1 mb-2"
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 400,
                  color: warningColors.labelColor,
                }}
              >
                <GoAlert size={15} aria-hidden="true" />
                Warnings
              </div>
              <div
                aria-hidden="true"
                style={{
                  fontSize: "1.75rem",
                  fontWeight: 600,
                  color: warningColors.valueColor,
                }}
              >
                {warnings}
              </div>
            </div>
          </div>

          <div className="col d-flex">
            <div
              className="rounded-3 px-3 py-3 w-100 d-flex flex-column align-items-center justify-content-center text-center"
              role="group"
              aria-label={`Certification Step: ${certificationStep || "none"}`}
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #ebebeb",
              }}
            >
              <div
                className="d-flex align-items-center gap-1 mb-2"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#475569",
                }}
              >
                <LuSlidersHorizontal size={15} aria-hidden="true" />
                Certification Step
              </div>
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                {certificationStep || "-"}
              </div>
            </div>
          </div>

          <div className="col d-flex">
            <div
              className="rounded-3 px-3 py-3 w-100 d-flex flex-column align-items-center justify-content-center text-center"
              role="group"
              aria-label={`Last Review Date: ${lastReviewDateString || "none"}`}
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #ebebeb",
              }}
            >
              <div
                className="d-flex align-items-center gap-1 mb-2"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#475569",
                }}
              >
                <LuCalendar size={15} aria-hidden="true" />
                Last Review Date
              </div>
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                {lastReviewDate || "-"}
              </div>
            </div>
          </div>

          <div className="col d-flex">
            <div
              className="rounded-3 px-3 py-3 w-100 d-flex flex-column align-items-center justify-content-center text-center"
              role="group"
              aria-label={`Last Reviewer: ${lastReviewer || "none"}`}
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #ebebeb",
              }}
            >
              <div
                className="d-flex align-items-center gap-1 mb-2"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#475569",
                }}
              >
                <LuUser size={15} aria-hidden="true" />
                Last Reviewer
              </div>
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                {lastReviewer || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
